import { Button, Input } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { DownloadIcon } from '@blocksuite/icons/rc';
import { useCallback, useState } from 'react';

import { useDataExport } from '../use-data-export';
import * as styles from './style.css';

export interface StepConfirmProps {
  email: string;
  hasOwnedWorkspaces: boolean;
  submitting: boolean;
  onBack: () => void;
  onCancel: () => void;
  onOpenWorkspaceBackup?: () => void;
  onSubmit: (reason: string | undefined) => Promise<void> | void;
}

// Step 2 — export, type-confirm and optional feedback. Email match is
// case-insensitive and trimmed. Submit stays disabled until the user
// explicitly retypes their address; Enter submits when valid.
export const StepConfirm = ({
  email,
  hasOwnedWorkspaces,
  submitting,
  onBack,
  onCancel,
  onOpenWorkspaceBackup,
  onSubmit,
}: StepConfirmProps) => {
  const t = useI18n();
  const { exportData, isExporting } = useDataExport();

  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  const matches = typed.trim().toLowerCase() === email.trim().toLowerCase();
  const canSubmit = matches && !submitting;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    void onSubmit(reason.trim() ? reason.trim() : undefined);
  }, [canSubmit, onSubmit, reason]);

  return (
    <div className={styles.dialogContent}>
      <div className={styles.stepHeader}>
        <span>
          {t['com.streamlain.setting.account.delete.dialog.step-indicator']({
            current: '2',
            total: '2',
          })}
        </span>
      </div>

      <h2 className={styles.sectionTitle}>
        {t['com.streamlain.setting.account.delete.step-confirm.title']()}
      </h2>

      <div className={styles.exportCard}>
        <div className={styles.exportCopy}>
          <strong>
            {t['com.streamlain.setting.account.delete.step-export.title']()}
          </strong>
          <span>{t['com.streamlain.settings.data-export.description']()}</span>
        </div>
        <Button
          onClick={exportData}
          loading={isExporting}
          data-testid="account-deletion-export-button"
          prefix={<DownloadIcon />}
        >
          {t['com.streamlain.settings.data-export.button']()}
        </Button>
      </div>

      {hasOwnedWorkspaces && onOpenWorkspaceBackup ? (
        <div className={styles.exportCard}>
          <div className={styles.exportCopy}>
            <strong>{t['Full Backup']()}</strong>
            <span>{t['Full Backup Description']()}</span>
          </div>
          <Button
            onClick={onOpenWorkspaceBackup}
            data-testid="account-deletion-workspace-backup-button"
          >
            {t['Full Backup']()}
          </Button>
        </div>
      ) : null}

      <div>
        <div className={styles.inputLabel}>
          {t[
            'com.streamlain.setting.account.delete.step-confirm.email-label'
          ]()}
        </div>
        <Input
          value={typed}
          onChange={setTyped}
          status={typed && !matches ? 'error' : 'default'}
          placeholder={email}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          disabled={submitting}
          onEnter={handleSubmit}
        />
      </div>

      <div>
        <label className={styles.inputLabel} htmlFor="account-deletion-reason">
          {t[
            'com.streamlain.setting.account.delete.step-confirm.reason-label'
          ]()}
        </label>
        <textarea
          id="account-deletion-reason"
          className={styles.reasonTextarea}
          value={reason}
          onChange={event => setReason(event.currentTarget.value)}
          placeholder={t[
            'com.streamlain.setting.account.delete.step-confirm.reason-placeholder'
          ]()}
          disabled={submitting}
          rows={5}
        />
      </div>

      <div className={styles.footer}>
        <Button variant="plain" onClick={onBack} disabled={submitting}>
          {t['com.streamlain.setting.account.delete.dialog.back']()}
        </Button>
        <div className={styles.footerActions}>
          <Button variant="plain" onClick={onCancel} disabled={submitting}>
            {t['com.streamlain.setting.account.delete.dialog.cancel']()}
          </Button>
          <Button
            variant="error"
            disabled={!canSubmit}
            loading={submitting}
            onClick={handleSubmit}
          >
            {t['com.streamlain.setting.account.delete.step-confirm.submit']()}
          </Button>
        </div>
      </div>
    </div>
  );
};
